<template>
  <div>
    <v-row>
      <v-col cols="6"><h1 class="font-weight-thin">Service Area Administration</h1></v-col>
    </v-row>
    <v-app-bar flat color="gray" dense>
      <v-toolbar-title class="text-h6 pl-0 font-weight-thin">
        Service Area List
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn @click="showCreateForm()" small outlined color="primary" data-cy="createButton">
        <v-icon class="mr-2" small>fas fa-plus</v-icon>Add
      </v-btn>
    </v-app-bar>
    <v-card class="elevation-0">
      <v-card-text>
        <v-checkbox
          v-model="includeInactive"
          @change="fetchList({ includeInactive })"
          label="Show inactive Service Areas"
          data-cy="include-inactive-toggle"
          dense
          class="mt-0"
        ></v-checkbox>
        <v-data-table
          :custom-filter="customFilter"
          :footer-props="{
            itemsPerPageOptions: [5, 10, 25, 50],
          }"
          :headers="headerList"
          :items="list"
          :items-per-page="25"
          :loading="loadingList"
          :search="search"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          data-cy="serviceAreaTable"
          fixed-header
          item-key="id"
        >
          <template v-slot:top>
            <v-row>
              <v-col sm="12" md="6">
                <v-text-field
                  class="ml-2"
                  clearable
                  data-cy="service-area-search"
                  dense
                  label="Search"
                  outlined
                  prepend-inner-icon="search"
                  single-line
                  v-model="search"
                ></v-text-field>
              </v-col>
            </v-row>
          </template>
          <template v-slot:item="{ item }">
            <tr :class="{ 'grey--text': item.active === 0 }">
              <td>
                <span role="img" :aria-label="item.active ? 'Active' : 'Inactive'">
                  <v-icon v-if="item.active" small color="primary">far fa-check-circle</v-icon>
                  <v-icon v-else small>fas fa-times</v-icon>
                </span>
              </td>
              <td>{{ item.id }}</td>
              <td>{{ item.name }}</td>
              <td>{{ item.careSetting }}</td>
              <td>
                <span
                  v-if="item.accountManager"
                  :class="{ 'grey--text text--darken-1 font-italic': item.accountManager.active === 0 }"
                >
                  {{ formatContactDisplay(item.accountManager) }}
                </span>
              </td>
              <td>
                <span
                  v-if="item.contactPrimary"
                  :class="{ 'grey--text text--darken-1 font-italic': item.contactPrimary.active === 0 }"
                >
                  {{ formatContactDisplay(item.contactPrimary) }}
                </span>
              </td>
              <td>
                <span
                  v-if="item.contactSecondary"
                  :class="{ 'grey--text text--darken-1 font-italic': item.contactSecondary.active === 0 }"
                >
                  {{ formatContactDisplay(item.contactSecondary) }}
                </span>
              </td>
              <td>
                <v-btn @click="showEditForm(item)" color="primary" outlined small>Edit</v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <admin-service-area-form v-model="showEdit" mode="edit"></admin-service-area-form>
    <admin-service-area-form v-model="showCreate" :serviceAreaList="list" mode="create"></admin-service-area-form>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";

import AdminServiceAreaForm from "./AdminServiceAreaForm";

export default {
  async created() {
    await this.fetchList({ includeInactive: this.includeInactive });
  },
  components: {
    AdminServiceAreaForm,
  },
  computed: mapState({
    list: state => state.admin.serviceArea.list,
    loadingList: state => state.admin.serviceArea.loadingList,
  }),
  data() {
    return {
      headerList: [
        {
          text: "Active",
          value: "active",
        },
        {
          text: "Id",
          value: "id",
        },
        {
          text: "Name",
          value: "name",
        },
        {
          text: "Care Setting",
          value: "careSetting",
        },
        {
          text: "Account Manager",
          value: "accountManager",
        },
        {
          text: "Primary Contact",
          value: "contactPrimary",
        },
        {
          text: "Secondary Contact",
          value: "contactSecondary",
        },
        {
          sortable: false,
          text: "Edit",
        },
      ],
      includeInactive: false,
      search: null,
      showCreate: false,
      showEdit: false,
      sortBy: "id",
      sortDesc: false,
    };
  },
  methods: {
    customFilter(value, search, item) {
      // If no search term, show all items
      if (!search) return true;

      const searchLower = search.toLowerCase();

      // Helper to safely check if a string contains the search term
      const contains = str => {
        if (!str) return false;
        return String(str)
          .toLowerCase()
          .includes(searchLower);
      };

      // Search through service area direct properties
      if (contains(item.id) || contains(item.name) || contains(item.careSetting)) {
        return true;
      }

      // Helper to search through user object properties
      const searchUser = user => {
        if (!user) return false;
        const fullName = `${user.firstName} ${user.lastName}`;
        return contains(user.firstName) || contains(user.lastName) || contains(fullName) || contains(user.email);
      };

      // Search through nested user objects
      return searchUser(item.accountManager) || searchUser(item.contactPrimary) || searchUser(item.contactSecondary);
    },
    formatContactDisplay(contact) {
      if (!contact) return "";

      const name = [contact.firstName, contact.lastName].filter(Boolean).join(" ");
      const email = contact.email ? ` (${contact.email})` : "";
      const inactive = contact.active === 0 ? " - Inactive" : "";

      return `${name}${email}${inactive}`;
    },
    showCreateForm() {
      this.setEdit({});
      this.showCreate = true;
    },
    showEditForm(serviceArea) {
      // Convert nested user objects back to username strings for the edit form
      const serviceAreaForEdit = {
        ...serviceArea,
        accountManager: serviceArea.accountManager?.username || null,
        active: Boolean(serviceArea.active),
        contactPrimary: serviceArea.contactPrimary?.username || null,
        contactSecondary: serviceArea.contactSecondary?.username || null,
      };
      this.setEdit(serviceAreaForEdit);
      this.showEdit = true;
    },
    ...mapActions({
      fetchList: "admin/serviceArea/fetchList",
    }),

    ...mapMutations({
      setEdit: "admin/serviceArea/setEdit",
    }),
  },
};
</script>
